import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { refreshExchangeRates } from "@/lib/localization.functions";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Languages, Coins, MapPin, Globe2, FileText,
  Plus, Pencil, Trash2, RefreshCcw, Download, Upload, Star, Sparkles,
} from "lucide-react";
import { translateBatch } from "@/lib/translate.functions";

export const Route = createFileRoute("/_authenticated/admin/localization")({
  component: LocalizationAdmin,
});

function LocalizationAdmin() {
  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Localization"
        description="Languages, currencies, exchange rates, regional defaults, and translation strings."
      />
      <Tabs defaultValue="languages" className="w-full">
        <PillTabsList search>
          <PillTabsTrigger value="languages"><Languages className="h-3.5 w-3.5 mr-1.5" />Languages</PillTabsTrigger>
          <PillTabsTrigger value="currencies"><Coins className="h-3.5 w-3.5 mr-1.5" />Currencies</PillTabsTrigger>
          <PillTabsTrigger value="pricing"><MapPin className="h-3.5 w-3.5 mr-1.5" />Regional pricing</PillTabsTrigger>
          <PillTabsTrigger value="defaults"><Globe2 className="h-3.5 w-3.5 mr-1.5" />Regional defaults</PillTabsTrigger>
          <PillTabsTrigger value="translations"><FileText className="h-3.5 w-3.5 mr-1.5" />Translations</PillTabsTrigger>
        </PillTabsList>
        <TabsContent value="languages" className="mt-4"><LanguagesCard /></TabsContent>
        <TabsContent value="currencies" className="mt-4"><CurrenciesCard /></TabsContent>
        <TabsContent value="pricing" className="mt-4"><RegionalPricingCard /></TabsContent>
        <TabsContent value="defaults" className="mt-4"><RegionalDefaultsCard /></TabsContent>
        <TabsContent value="translations" className="mt-4"><TranslationsCard /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Types
// ============================================================

type Locale = {
  code: string; name: string; native_name: string | null;
  enabled: boolean; is_default: boolean; rtl: boolean; sort_order: number;
};
type Currency = {
  code: string; name: string; symbol: string; decimals: number;
  enabled: boolean; is_default: boolean;
  rate_to_base: number; rate_updated_at: string | null;
};
type RegionalPricing = {
  country_code: string; currency_code: string;
  price_multiplier: number; notes: string | null;
};
type RegionalDefault = {
  country_code: string;
  default_locale: string | null; default_currency: string | null;
  tax_rate_pct: number; shipping_flat_cents: number; notes: string | null;
};
type Translation = {
  id: string; namespace: string; key: string; locale: string; value: string;
};

// ============================================================
// Languages
// ============================================================

function LanguagesCard() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Locale | null>(null);
  const [open, setOpen] = useState(false);

  const q = useQuery({
    queryKey: ["locales"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locales").select("*").order("sort_order").order("code");
      if (error) throw error;
      return data as Locale[];
    },
  });

  const upsert = useMutation({
    mutationFn: async ({ locale: l, isNew }: { locale: Locale; isNew: boolean }) => {
      const { error } = await supabase.from("locales").upsert(l);
      if (error) throw error;
      if (!isNew) return { copied: 0 };
      // Auto-seed: copy all strings from default locale into the new locale.
      const { data: def } = await supabase
        .from("locales").select("code").eq("is_default", true).maybeSingle();
      const defCode = def?.code;
      if (!defCode || defCode === l.code) return { copied: 0 };
      const { data: src } = await supabase
        .from("translation_strings").select("namespace,key,value").eq("locale", defCode);
      if (!src?.length) return { copied: 0 };
      const rows = src.map((r: any) => ({
        namespace: r.namespace, key: r.key, locale: l.code, value: r.value,
      }));
      const { error: e2 } = await supabase
        .from("translation_strings").upsert(rows, { onConflict: "namespace,key,locale" });
      if (e2) throw e2;
      return { copied: rows.length };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["locales"] });
      qc.invalidateQueries({ queryKey: ["translations"] });
      toast.success(res.copied ? `Saved — seeded ${res.copied} string(s) from default locale` : "Saved");
      setOpen(false);
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  async function exportLocale(code: string) {
    const { data, error } = await supabase
      .from("translation_strings").select("namespace,key,locale,value")
      .eq("locale", code).order("namespace").order("key");
    if (error) { toast.error(error.message); return; }
    if (!data || data.length === 0) {
      toast.error(`No translation strings exist for "${code}" yet. Add strings in the Translations tab first.`);
      return;
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `translations-${code}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.length} string(s)`);
  }

  const del = useMutation({
    mutationFn: async (code: string) => {
      const { error } = await supabase.from("locales").delete().eq("code", code);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["locales"] }),
    onError: (e: any) => toast.error(e.message ?? "Delete failed"),
  });

  const setDefault = useMutation({
    mutationFn: async (code: string) => {
      // Clear current default then set new one (unique partial index allows only one).
      const { error: e1 } = await supabase.from("locales").update({ is_default: false }).eq("is_default", true);
      if (e1) throw e1;
      const { error: e2 } = await supabase.from("locales").update({ is_default: true, enabled: true }).eq("code", code);
      if (e2) throw e2;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["locales"] }); toast.success("Default updated"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  const toggle = useMutation({
    mutationFn: async (l: Locale) => {
      const { error } = await supabase.from("locales").update({ enabled: !l.enabled }).eq("code", l.code);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["locales"] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          Supported languages
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                <Plus className="h-4 w-4 mr-1" />Add language
              </Button>
            </DialogTrigger>
            <LocaleDialog
              key={editing?.code ?? "new"}
              initial={editing}
              onSubmit={(l) => upsert.mutate({ locale: l, isNew: !editing })}
              busy={upsert.isPending}
            />
          </Dialog>
        </CardTitle>
        <CardDescription>
          Add the locales your site supports. Mark one as default. Toggle RTL for right-to-left scripts.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Native</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>RTL</TableHead>
              <TableHead>Default</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading && <TableRow><TableCell colSpan={7} className="text-muted-foreground text-sm">Loading…</TableCell></TableRow>}
            {q.data?.map((l) => (
              <TableRow key={l.code}>
                <TableCell className="font-mono text-xs">{l.code}</TableCell>
                <TableCell>{l.name}</TableCell>
                <TableCell className="text-muted-foreground">{l.native_name ?? "—"}</TableCell>
                <TableCell><Switch checked={l.enabled} onCheckedChange={() => toggle.mutate(l)} /></TableCell>
                <TableCell>{l.rtl ? <Badge variant="secondary">RTL</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell>
                  {l.is_default ? (
                    <Badge><Star className="h-3 w-3 mr-1" />Default</Badge>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setDefault.mutate(l.code)}>Make default</Button>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Export all strings" onClick={() => exportLocale(l.code)}>
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(l); setOpen(true); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" disabled={l.is_default}
                    onClick={() => { if (confirm(`Delete locale "${l.code}"?`)) del.mutate(l.code); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {q.data && q.data.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-muted-foreground text-sm">No locales yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function LocaleDialog({
  initial, onSubmit, busy,
}: { initial: Locale | null; onSubmit: (l: Locale) => void; busy: boolean }) {
  const [s, setS] = useState<Locale>(initial ?? {
    code: "", name: "", native_name: "", enabled: true, is_default: false, rtl: false, sort_order: 0,
  });
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{initial ? "Edit language" : "Add language"}</DialogTitle>
        <DialogDescription>Use BCP-47 codes like <code>en</code>, <code>ar-SA</code>, <code>fr-CA</code>.</DialogDescription>
      </DialogHeader>
      <div className="grid gap-3">
        <div className="grid gap-2">
          <Label>Code</Label>
          <Input value={s.code} disabled={!!initial}
            onChange={(e) => setS({ ...s, code: e.target.value.trim() })} placeholder="en" />
        </div>
        <div className="grid gap-2">
          <Label>Name (English)</Label>
          <Input value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} placeholder="English" />
        </div>
        <div className="grid gap-2">
          <Label>Native name</Label>
          <Input value={s.native_name ?? ""} onChange={(e) => setS({ ...s, native_name: e.target.value })} placeholder="English" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} />Enabled</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={s.rtl} onCheckedChange={(v) => setS({ ...s, rtl: v })} />RTL</label>
          <div className="grid gap-1">
            <Label className="text-xs">Sort</Label>
            <Input type="number" value={s.sort_order} onChange={(e) => setS({ ...s, sort_order: Number(e.target.value) || 0 })} />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={() => onSubmit(s)} disabled={busy || !s.code || !s.name}>Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ============================================================
// Currencies
// ============================================================

function CurrenciesCard() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Currency | null>(null);
  const [open, setOpen] = useState(false);
  const refreshFn = useServerFn(refreshExchangeRates);

  const q = useQuery({
    queryKey: ["currencies"],
    queryFn: async () => {
      const { data, error } = await supabase.from("currencies").select("*").order("code");
      if (error) throw error;
      return data as Currency[];
    },
  });

  const upsert = useMutation({
    mutationFn: async (c: Currency) => {
      const { error } = await supabase.from("currencies").upsert(c);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["currencies"] }); toast.success("Saved"); setOpen(false); },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const del = useMutation({
    mutationFn: async (code: string) => {
      const { error } = await supabase.from("currencies").delete().eq("code", code);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["currencies"] }),
    onError: (e: any) => toast.error(e.message ?? "Delete failed"),
  });

  const toggle = useMutation({
    mutationFn: async (c: Currency) => {
      const { error } = await supabase.from("currencies").update({ enabled: !c.enabled }).eq("code", c.code);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["currencies"] }),
  });

  const setDefault = useMutation({
    mutationFn: async (code: string) => {
      const { error: e1 } = await supabase.from("currencies").update({ is_default: false }).eq("is_default", true);
      if (e1) throw e1;
      const { error: e2 } = await supabase.from("currencies").update({ is_default: true, enabled: true, rate_to_base: 1 }).eq("code", code);
      if (e2) throw e2;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["currencies"] }); toast.success("Default updated"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  const refresh = useMutation({
    mutationFn: () => refreshFn(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["currencies"] });
      if (res.error) toast.error(res.error);
      else toast.success(`Updated ${res.updated} currency rate(s) — base ${res.base}`);
    },
    onError: (e: any) => toast.error(e.message ?? "Refresh failed"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          Currencies
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
              <RefreshCcw className={`h-4 w-4 mr-1 ${refresh.isPending ? "animate-spin" : ""}`} />
              Refresh rates
            </Button>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                  <Plus className="h-4 w-4 mr-1" />Add currency
                </Button>
              </DialogTrigger>
              <CurrencyDialog
                key={editing?.code ?? "new"}
                initial={editing}
                onSubmit={(c) => upsert.mutate(c)}
                busy={upsert.isPending}
              />
            </Dialog>
          </div>
        </CardTitle>
        <CardDescription>
          Rates are stored relative to the default currency. "Refresh rates" pulls live values from open.er-api.com (free, no API key).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Symbol</TableHead>
              <TableHead>Decimals</TableHead>
              <TableHead>Rate → base</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>Default</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading && <TableRow><TableCell colSpan={9} className="text-muted-foreground text-sm">Loading…</TableCell></TableRow>}
            {q.data?.map((c) => (
              <TableRow key={c.code}>
                <TableCell className="font-mono text-xs">{c.code}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.symbol}</TableCell>
                <TableCell>{c.decimals}</TableCell>
                <TableCell className="font-mono text-xs">{Number(c.rate_to_base).toFixed(6)}</TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {c.rate_updated_at ? new Date(c.rate_updated_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell><Switch checked={c.enabled} onCheckedChange={() => toggle.mutate(c)} /></TableCell>
                <TableCell>
                  {c.is_default ? (
                    <Badge><Star className="h-3 w-3 mr-1" />Default</Badge>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setDefault.mutate(c.code)}>Make default</Button>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setOpen(true); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" disabled={c.is_default}
                    onClick={() => { if (confirm(`Delete currency "${c.code}"?`)) del.mutate(c.code); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {q.data && q.data.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-muted-foreground text-sm">No currencies yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function CurrencyDialog({
  initial, onSubmit, busy,
}: { initial: Currency | null; onSubmit: (c: Currency) => void; busy: boolean }) {
  const [s, setS] = useState<Currency>(initial ?? {
    code: "", name: "", symbol: "", decimals: 2,
    enabled: true, is_default: false, rate_to_base: 1, rate_updated_at: null,
  });
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{initial ? "Edit currency" : "Add currency"}</DialogTitle>
        <DialogDescription>ISO 4217 currency code (e.g. <code>EUR</code>, <code>GBP</code>, <code>JPY</code>).</DialogDescription>
      </DialogHeader>
      <div className="grid gap-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="grid gap-2">
            <Label>Code</Label>
            <Input value={s.code} disabled={!!initial}
              onChange={(e) => setS({ ...s, code: e.target.value.toUpperCase().trim() })} placeholder="EUR" />
          </div>
          <div className="grid gap-2">
            <Label>Symbol</Label>
            <Input value={s.symbol} onChange={(e) => setS({ ...s, symbol: e.target.value })} placeholder="€" />
          </div>
          <div className="grid gap-2">
            <Label>Decimals</Label>
            <Input type="number" min={0} max={6} value={s.decimals}
              onChange={(e) => setS({ ...s, decimals: Number(e.target.value) || 0 })} />
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Name</Label>
          <Input value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} placeholder="Euro" />
        </div>
        <div className="grid gap-2">
          <Label>Rate to base</Label>
          <Input type="number" step="0.000001" value={s.rate_to_base}
            onChange={(e) => setS({ ...s, rate_to_base: Number(e.target.value) || 0 })} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} />Enabled
        </label>
      </div>
      <DialogFooter>
        <Button onClick={() => onSubmit(s)} disabled={busy || !s.code || !s.name}>Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ============================================================
// Regional pricing
// ============================================================

function RegionalPricingCard() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RegionalPricing | null>(null);
  const [open, setOpen] = useState(false);

  const q = useQuery({
    queryKey: ["regional_pricing"],
    queryFn: async () => {
      const { data, error } = await supabase.from("regional_pricing").select("*").order("country_code");
      if (error) throw error;
      return data as RegionalPricing[];
    },
  });

  const currencies = useQuery({
    queryKey: ["currencies", "enabled"],
    queryFn: async () => {
      const { data } = await supabase.from("currencies").select("code,name").eq("enabled", true).order("code");
      return (data ?? []) as { code: string; name: string }[];
    },
  });

  const upsert = useMutation({
    mutationFn: async (r: RegionalPricing) => {
      const { error } = await supabase.from("regional_pricing").upsert(r);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["regional_pricing"] }); toast.success("Saved"); setOpen(false); },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const del = useMutation({
    mutationFn: async (cc: string) => {
      const { error } = await supabase.from("regional_pricing").delete().eq("country_code", cc);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regional_pricing"] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          Regional pricing
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                <Plus className="h-4 w-4 mr-1" />Add country
              </Button>
            </DialogTrigger>
            <RegionalPricingDialog
              key={editing?.country_code ?? "new"}
              initial={editing}
              currencies={currencies.data ?? []}
              onSubmit={(r) => upsert.mutate(r)}
              busy={upsert.isPending}
            />
          </Dialog>
        </CardTitle>
        <CardDescription>
          Override pricing per ISO-2 country code. Multiplier is applied to the base price (e.g. 0.9 = 10% off).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Country</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>Multiplier</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data?.map((r) => (
              <TableRow key={r.country_code}>
                <TableCell className="font-mono text-xs">{r.country_code}</TableCell>
                <TableCell>{r.currency_code}</TableCell>
                <TableCell>{Number(r.price_multiplier).toFixed(3)}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{r.notes ?? "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(r); setOpen(true); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon"
                    onClick={() => { if (confirm(`Delete pricing for "${r.country_code}"?`)) del.mutate(r.country_code); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {q.data && q.data.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-muted-foreground text-sm">No country overrides yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function RegionalPricingDialog({
  initial, currencies, onSubmit, busy,
}: {
  initial: RegionalPricing | null;
  currencies: { code: string; name: string }[];
  onSubmit: (r: RegionalPricing) => void; busy: boolean;
}) {
  const [s, setS] = useState<RegionalPricing>(initial ?? {
    country_code: "", currency_code: currencies[0]?.code ?? "USD", price_multiplier: 1, notes: "",
  });
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{initial ? "Edit pricing" : "Add country pricing"}</DialogTitle>
        <DialogDescription>ISO-2 country codes (e.g. <code>US</code>, <code>DE</code>, <code>JP</code>).</DialogDescription>
      </DialogHeader>
      <div className="grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-2">
            <Label>Country</Label>
            <Input value={s.country_code} disabled={!!initial}
              onChange={(e) => setS({ ...s, country_code: e.target.value.toUpperCase().trim() })} placeholder="DE" />
          </div>
          <div className="grid gap-2">
            <Label>Currency</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={s.currency_code} onChange={(e) => setS({ ...s, currency_code: e.target.value })}>
              {currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Price multiplier</Label>
          <Input type="number" step="0.01" value={s.price_multiplier}
            onChange={(e) => setS({ ...s, price_multiplier: Number(e.target.value) || 0 })} />
        </div>
        <div className="grid gap-2">
          <Label>Notes</Label>
          <Textarea rows={2} value={s.notes ?? ""} onChange={(e) => setS({ ...s, notes: e.target.value })} />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={() => onSubmit(s)} disabled={busy || !s.country_code || !s.currency_code}>Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ============================================================
// Regional defaults
// ============================================================

function RegionalDefaultsCard() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RegionalDefault | null>(null);
  const [open, setOpen] = useState(false);

  const q = useQuery({
    queryKey: ["regional_defaults"],
    queryFn: async () => {
      const { data, error } = await supabase.from("regional_defaults").select("*").order("country_code");
      if (error) throw error;
      return data as RegionalDefault[];
    },
  });

  const locales = useQuery({
    queryKey: ["locales", "enabled"],
    queryFn: async () => {
      const { data } = await supabase.from("locales").select("code,name").eq("enabled", true).order("code");
      return (data ?? []) as { code: string; name: string }[];
    },
  });

  const currencies = useQuery({
    queryKey: ["currencies", "enabled"],
    queryFn: async () => {
      const { data } = await supabase.from("currencies").select("code,name").eq("enabled", true).order("code");
      return (data ?? []) as { code: string; name: string }[];
    },
  });

  const upsert = useMutation({
    mutationFn: async (r: RegionalDefault) => {
      const { error } = await supabase.from("regional_defaults").upsert(r);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["regional_defaults"] }); toast.success("Saved"); setOpen(false); },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const del = useMutation({
    mutationFn: async (cc: string) => {
      const { error } = await supabase.from("regional_defaults").delete().eq("country_code", cc);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regional_defaults"] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          Country defaults
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                <Plus className="h-4 w-4 mr-1" />Add country
              </Button>
            </DialogTrigger>
            <RegionalDefaultsDialog
              key={editing?.country_code ?? "new"}
              initial={editing}
              locales={locales.data ?? []}
              currencies={currencies.data ?? []}
              onSubmit={(r) => upsert.mutate(r)}
              busy={upsert.isPending}
            />
          </Dialog>
        </CardTitle>
        <CardDescription>
          Default locale, currency, tax rate, and flat shipping per country.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Country</TableHead>
              <TableHead>Locale</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>Tax %</TableHead>
              <TableHead>Shipping (¢)</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data?.map((r) => (
              <TableRow key={r.country_code}>
                <TableCell className="font-mono text-xs">{r.country_code}</TableCell>
                <TableCell>{r.default_locale ?? "—"}</TableCell>
                <TableCell>{r.default_currency ?? "—"}</TableCell>
                <TableCell>{Number(r.tax_rate_pct).toFixed(2)}</TableCell>
                <TableCell>{r.shipping_flat_cents}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(r); setOpen(true); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon"
                    onClick={() => { if (confirm(`Delete defaults for "${r.country_code}"?`)) del.mutate(r.country_code); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {q.data && q.data.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-muted-foreground text-sm">No country defaults yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function RegionalDefaultsDialog({
  initial, locales, currencies, onSubmit, busy,
}: {
  initial: RegionalDefault | null;
  locales: { code: string; name: string }[];
  currencies: { code: string; name: string }[];
  onSubmit: (r: RegionalDefault) => void; busy: boolean;
}) {
  const [s, setS] = useState<RegionalDefault>(initial ?? {
    country_code: "",
    default_locale: locales[0]?.code ?? null,
    default_currency: currencies[0]?.code ?? null,
    tax_rate_pct: 0, shipping_flat_cents: 0, notes: "",
  });
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{initial ? "Edit defaults" : "Add country defaults"}</DialogTitle>
        <DialogDescription>ISO-2 country code.</DialogDescription>
      </DialogHeader>
      <div className="grid gap-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="grid gap-2">
            <Label>Country</Label>
            <Input value={s.country_code} disabled={!!initial}
              onChange={(e) => setS({ ...s, country_code: e.target.value.toUpperCase().trim() })} placeholder="US" />
          </div>
          <div className="grid gap-2">
            <Label>Default locale</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={s.default_locale ?? ""} onChange={(e) => setS({ ...s, default_locale: e.target.value || null })}>
              <option value="">—</option>
              {locales.map((l) => <option key={l.code} value={l.code}>{l.code} — {l.name}</option>)}
            </select>
          </div>
          <div className="grid gap-2">
            <Label>Default currency</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={s.default_currency ?? ""} onChange={(e) => setS({ ...s, default_currency: e.target.value || null })}>
              <option value="">—</option>
              {currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-2">
            <Label>Tax rate (%)</Label>
            <Input type="number" step="0.01" value={s.tax_rate_pct}
              onChange={(e) => setS({ ...s, tax_rate_pct: Number(e.target.value) || 0 })} />
          </div>
          <div className="grid gap-2">
            <Label>Flat shipping (cents)</Label>
            <Input type="number" value={s.shipping_flat_cents}
              onChange={(e) => setS({ ...s, shipping_flat_cents: Number(e.target.value) || 0 })} />
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Notes</Label>
          <Textarea rows={2} value={s.notes ?? ""} onChange={(e) => setS({ ...s, notes: e.target.value })} />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={() => onSubmit(s)} disabled={busy || !s.country_code}>Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ============================================================
// Translations
// ============================================================

function TranslationsCard() {
  const qc = useQueryClient();
  const [namespace, setNamespace] = useState<string>("ui");
  const [locale, setLocale] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Translation | null>(null);

  const locales = useQuery({
    queryKey: ["locales", "all"],
    queryFn: async () => {
      const { data } = await supabase.from("locales").select("code,name").order("code");
      return (data ?? []) as { code: string; name: string }[];
    },
  });

  useEffect(() => {
    if (!locale && locales.data && locales.data.length) setLocale(locales.data[0].code);
  }, [locales.data, locale]);

  const q = useQuery({
    queryKey: ["translations", namespace, locale],
    enabled: !!locale,
    queryFn: async () => {
      let qb = supabase.from("translation_strings").select("*").order("key");
      if (namespace) qb = qb.eq("namespace", namespace);
      if (locale) qb = qb.eq("locale", locale);
      const { data, error } = await qb;
      if (error) throw error;
      return data as Translation[];
    },
  });

  const namespacesQ = useQuery({
    queryKey: ["translations", "namespaces"],
    queryFn: async () => {
      const { data } = await supabase.from("translation_strings").select("namespace");
      const set = new Set<string>(["ui"]);
      (data ?? []).forEach((r: any) => set.add(r.namespace));
      return Array.from(set).sort();
    },
  });

  const filtered = useMemo(() => {
    if (!q.data) return [];
    const s = search.trim().toLowerCase();
    if (!s) return q.data;
    return q.data.filter((t) => t.key.toLowerCase().includes(s) || t.value.toLowerCase().includes(s));
  }, [q.data, search]);

  const upsert = useMutation({
    mutationFn: async (t: Partial<Translation> & { namespace: string; key: string; locale: string }) => {
      const { error } = await supabase
        .from("translation_strings")
        .upsert(t, { onConflict: "namespace,key,locale" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["translations"] });
      toast.success("Saved");
      setOpen(false);
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("translation_strings").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["translations"] }),
  });

  // Default locale (used as source for auto-translation of missing keys)
  const defaultLocaleQ = useQuery({
    queryKey: ["locales", "default"],
    queryFn: async () => {
      const { data } = await supabase
        .from("locales").select("code").eq("is_default", true).maybeSingle();
      return (data?.code as string | undefined) ?? "en";
    },
  });
  const defaultCode = defaultLocaleQ.data ?? "en";

  // Compute missing keys: keys present (non-empty) in default locale but
  // absent or empty in the currently-selected locale, within this namespace.
  const missingQ = useQuery({
    queryKey: ["translations", "missing", namespace, locale, defaultCode],
    enabled: !!locale && !!defaultCode && locale !== defaultCode,
    queryFn: async () => {
      const { data: src, error } = await supabase
        .from("translation_strings")
        .select("key,value")
        .eq("namespace", namespace)
        .eq("locale", defaultCode);
      if (error) throw error;
      const currentMap = new Map((q.data ?? []).map((t) => [t.key, (t.value ?? "").trim()]));
      return (src ?? [])
        .filter((r: any) => (r.value ?? "").trim().length > 0)
        .filter((r: any) => !(currentMap.get(r.key) ?? "").length)
        .map((r: any) => ({ key: r.key as string, source: r.value as string }));
    },
  });

  const translateMissing = useMutation({
    mutationFn: async () => {
      const items = missingQ.data ?? [];
      if (!items.length) return { count: 0 };
      // Batch to keep each request small (translateBatch caps at 80 items / 500 chars).
      const chunkSize = 40;
      let inserted = 0;
      for (let i = 0; i < items.length; i += chunkSize) {
        const chunk = items.slice(i, i + chunkSize);
        const texts = chunk.map((c) => c.source.slice(0, 500));
        const res = await translateBatch({ data: { target: locale, texts } });
        const rows = chunk.map((c, idx) => ({
          namespace, key: c.key, locale,
          value: res.translations[idx] ?? c.source,
        }));
        const { error } = await supabase
          .from("translation_strings")
          .upsert(rows, { onConflict: "namespace,key,locale" });
        if (error) throw error;
        inserted += rows.length;
      }
      return { count: inserted };
    },
    onSuccess: (res) => {
      toast.success(res.count ? `Translated ${res.count} missing string(s)` : "No missing strings");
      qc.invalidateQueries({ queryKey: ["translations"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Auto-translate failed"),
  });


  function doExport() {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `translations-${namespace}-${locale}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function doImport(file: File) {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const text = String(reader.result ?? "").trim();
        if (!text) { toast.error("File is empty"); return; }
        const parsed = JSON.parse(text);
        const rows: Array<Pick<Translation, "namespace" | "key" | "locale" | "value">> = [];

        const pushRow = (ns: any, k: any, lc: any, v: any) => {
          const key = String(k ?? "").trim();
          const loc = String(lc ?? "").trim();
          if (!key || !loc) return;
          rows.push({
            namespace: String(ns ?? namespace ?? "ui").trim() || "ui",
            key, locale: loc, value: v == null ? "" : String(v),
          });
        };

        if (Array.isArray(parsed)) {
          // [{ namespace, key, locale, value }, ...]
          parsed.forEach((r: any) => pushRow(r?.namespace, r?.key, r?.locale, r?.value));
        } else if (parsed && typeof parsed === "object") {
          const entries = Object.entries(parsed as Record<string, any>);
          const allScalar = entries.every(([, v]) => v == null || typeof v !== "object");
          if (allScalar) {
            // Flat { key: value, ... } — use current namespace + locale
            entries.forEach(([k, v]) => pushRow(namespace, k, locale, v));
          } else {
            // Nested { namespace: { key: value } } — use current locale
            entries.forEach(([ns, kv]) => {
              if (kv && typeof kv === "object") {
                Object.entries(kv as Record<string, any>).forEach(([k, v]) => pushRow(ns, k, locale, v));
              }
            });
          }
        }

        if (!rows.length) {
          toast.error(
            Array.isArray(parsed) && parsed.length === 0
              ? "File is an empty array — there were no strings to export. Add translation strings in the Translations tab first."
              : "No valid rows in file. Expected JSON array of {namespace,key,locale,value} or { key: value } object."
          );
          return;
        }
        const { error } = await supabase
          .from("translation_strings")
          .upsert(rows, { onConflict: "namespace,key,locale" });
        if (error) throw error;
        toast.success(`Imported ${rows.length} string(s)`);
        qc.invalidateQueries({ queryKey: ["translations"] });
      } catch (e: any) {
        toast.error(e?.message ?? "Import failed");
      }
    };
    reader.readAsText(file);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            Translation strings
            {locale && locale !== defaultCode && (missingQ.data?.length ?? 0) > 0 && (
              <Badge variant="outline" className="text-amber-600 border-amber-500/40 bg-amber-500/10">
                {missingQ.data!.length} missing
              </Badge>
            )}
          </span>
          <div className="flex flex-wrap gap-2">
            {locale && locale !== defaultCode && (
              <Button
                size="sm"
                variant="default"
                onClick={() => translateMissing.mutate()}
                disabled={translateMissing.isPending || !(missingQ.data?.length)}
                title={`Auto-translate keys present in "${defaultCode}" but empty in "${locale}"`}
              >
                <Sparkles className="h-4 w-4 mr-1" />
                {translateMissing.isPending
                  ? "Translating…"
                  : `Translate missing${missingQ.data?.length ? ` (${missingQ.data.length})` : ""}`}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={doExport} disabled={!filtered.length}>
              <Download className="h-4 w-4 mr-1" />Export
            </Button>
            <Button size="sm" variant="outline" asChild>
              <label className="cursor-pointer">
                <Upload className="h-4 w-4 mr-1" />Import
                <input type="file" accept="application/json" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) doImport(f); e.currentTarget.value = ""; }} />
              </label>
            </Button>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                  <Plus className="h-4 w-4 mr-1" />New string
                </Button>
              </DialogTrigger>
              <TranslationDialog
                key={editing?.id ?? "new"}
                initial={editing}
                defaultNamespace={namespace}
                defaultLocale={locale}
                locales={locales.data ?? []}
                namespaces={namespacesQ.data ?? ["ui"]}
                onSubmit={(t) => upsert.mutate(t)}
                busy={upsert.isPending}
              />
            </Dialog>
          </div>
        </CardTitle>
        <CardDescription>
          Strings are grouped by namespace + locale, keyed by an arbitrary string (e.g. <code>checkout.cta</code>).
          Import JSON as an array of <code>{`{namespace,key,locale,value}`}</code> or a flat <code>{`{key:value}`}</code> object.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="grid gap-2">
            <Label>Namespace</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={namespace} onChange={(e) => setNamespace(e.target.value)}>
              {(namespacesQ.data ?? ["ui"]).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="grid gap-2">
            <Label>Locale</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={locale} onChange={(e) => setLocale(e.target.value)}>
              {(locales.data ?? []).map((l) => <option key={l.code} value={l.code}>{l.code} — {l.name}</option>)}
            </select>
          </div>
          <div className="grid gap-2">
            <Label>Search</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="key or value…" />
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/3">Key</TableHead>
              <TableHead>Value</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading && <TableRow><TableCell colSpan={3} className="text-muted-foreground text-sm">Loading…</TableCell></TableRow>}
            {filtered.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-mono text-xs">{t.key}</TableCell>
                <TableCell className="text-sm">{t.value || <span className="text-muted-foreground italic">empty</span>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(t); setOpen(true); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon"
                    onClick={() => { if (confirm(`Delete "${t.key}"?`)) del.mutate(t.id); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!q.isLoading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-muted-foreground text-sm">No strings.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function TranslationDialog({
  initial, defaultNamespace, defaultLocale, locales, namespaces, onSubmit, busy,
}: {
  initial: Translation | null;
  defaultNamespace: string;
  defaultLocale: string;
  locales: { code: string; name: string }[];
  namespaces: string[];
  onSubmit: (t: Partial<Translation> & { namespace: string; key: string; locale: string; value: string }) => void;
  busy: boolean;
}) {
  const [s, setS] = useState({
    id: initial?.id,
    namespace: initial?.namespace ?? defaultNamespace ?? "ui",
    key: initial?.key ?? "",
    locale: initial?.locale ?? defaultLocale ?? "en",
    value: initial?.value ?? "",
  });
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{initial ? "Edit string" : "New translation string"}</DialogTitle>
        <DialogDescription>Stored uniquely by (namespace, key, locale).</DialogDescription>
      </DialogHeader>
      <div className="grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-2">
            <Label>Namespace</Label>
            <Input list="ns-list" value={s.namespace}
              onChange={(e) => setS({ ...s, namespace: e.target.value })} placeholder="ui" />
            <datalist id="ns-list">{namespaces.map((n) => <option key={n} value={n} />)}</datalist>
          </div>
          <div className="grid gap-2">
            <Label>Locale</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={s.locale} onChange={(e) => setS({ ...s, locale: e.target.value })}>
              {locales.map((l) => <option key={l.code} value={l.code}>{l.code} — {l.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Key</Label>
          <Input value={s.key} onChange={(e) => setS({ ...s, key: e.target.value })} placeholder="checkout.cta" />
        </div>
        <div className="grid gap-2">
          <Label>Value</Label>
          <Textarea rows={3} value={s.value} onChange={(e) => setS({ ...s, value: e.target.value })} />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={() => onSubmit(s)} disabled={busy || !s.namespace || !s.key || !s.locale}>Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}
